import { Component, OnInit } from '@angular/core';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FileUploadModule } from 'primeng/fileupload';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ProgressBarModule } from 'primeng/progressbar';
import { TextareaModule } from 'primeng/textarea';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

import { InteractionService, IInteraction } from '../../../core/services/interaction.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, ConfirmationService } from 'primeng/api';
import { CustomConfirmModalComponent } from '../../../shared/components/custom-confirm-modal/custom-confirm-modal.component';
import { LoadingSpinnerComponent } from '../../../shared/components/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-shedule-interaction',
  imports: [ToastModule, CommonModule, CardModule, InputTextModule, SelectModule, ButtonModule, ReactiveFormsModule, FileUploadModule, RadioButtonModule, ProgressBarModule, TextareaModule, ConfirmDialogModule, CustomConfirmModalComponent, LoadingSpinnerComponent],
  templateUrl: './shedule-interaction.html',
  styleUrl: './shedule-interaction.scss',
  providers: [MessageService, ConfirmationService]
})
export class SheduleInteraction implements OnInit {
  sheduleInteractionForm: FormGroup;
  reservationId: string = '';
  isSubmitting: boolean = false;
  isMainButtonLoading = false;
  isJustSaveButtonLoading = false;
  
  // Custom modal properties
  showUpcomingInteractionsModal: boolean = false;
  upcomingInteractionsMessage: string = '';
  pendingFormValue: any = null;
  pendingUpcomingInteractions: IInteraction[] = [];
  
  // File upload properties
  uploadedFile: File | null = null;
  uploadedFileUrl: string | null = null;
  isFileUploading: boolean = false;
  dragOver: boolean = false;
  
  interactionTypeOptions: any[] = [
    { label: 'Sales Call', value: 'sales_call' },
    { label: 'Demo', value: 'demo' },
    { label: 'Service Call', value: 'service_call' },
    { label: 'Follow-up Visit', value: 'follow_up' },
    { label: 'Emergency Call', value: 'emergency' }
  ];

  reservationStatusOptions: any[] = [
    // { label: 'Expired', value: 'Expired' },
    { label: 'Converted', value: 'Converted' },
    { label: 'Not Interested', value: 'Not Interested' },
    // { label: 'Pending', value: 'Pending' },
    { label: 'Interested', value: 'Interested' }
  ];

  constructor(
    private fb: FormBuilder, 
    private interactionService: InteractionService,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {
    this.sheduleInteractionForm = this.fb.group({
      interactionType: ['', Validators.required],
      interactionDate: ['', Validators.required],
      interactionTime: ['', Validators.required],
      interactionStatus: ['upcoming', Validators.required],
      interactionNotes: [''],
      documents: [[]],
      reservationStatus: [''] // Start with empty value
    });
  }

  ngOnInit(): void {
    this.getReservationIdFromUrl();
    this.setupFormValidation();
  }

  getReservationIdFromUrl(): void {
    this.route.params.subscribe(params => {
      this.reservationId = params['id'];
    });
  }

  setupFormValidation(): void {
    // Listen to interaction status changes to update reservation status validation
    this.sheduleInteractionForm.get('interactionStatus')?.valueChanges.subscribe(status => {
      const reservationStatusControl = this.sheduleInteractionForm.get('reservationStatus');
      
      if (status === 'completed') {
        // Make reservation status required when interaction status is completed
        reservationStatusControl?.setValidators([Validators.required]);
        reservationStatusControl?.updateValueAndValidity();
      } else {
        // Remove required validation when interaction status is not completed
        reservationStatusControl?.clearValidators();
        reservationStatusControl?.updateValueAndValidity();
      }
    });

    // Set initial validation state based on current interaction status
    const currentStatus = this.sheduleInteractionForm.get('interactionStatus')?.value;
    if (currentStatus === 'completed') {
      const reservationStatusControl = this.sheduleInteractionForm.get('reservationStatus');
      reservationStatusControl?.setValidators([Validators.required]);
      reservationStatusControl?.updateValueAndValidity();
    }
  }

  onSubmit(): void {
    // Mark all fields as touched to show validation errors
    this.sheduleInteractionForm.markAllAsTouched();
    
    if (this.sheduleInteractionForm.valid && this.reservationId) {
      const formValue = this.sheduleInteractionForm.value;
      
      // Check if reservation status is "Converted"
      if (formValue.reservationStatus === 'Converted') {
        this.showConvertedConfirmation(formValue);
      } else {
        this.processInteraction(formValue);
      }
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields.'
      });
    }
  }

  private showConvertedConfirmation(formValue: any): void {
    // First check if there are any upcoming interactions for this reservation
    this.checkUpcomingInteractionsBeforeConversion(formValue);
  }

  private checkUpcomingInteractionsBeforeConversion(formValue: any): void {
    if (!this.reservationId) {
      console.error('Reservation ID is not available');
      this.showConversionConfirmation(formValue);
      return;
    }

    this.interactionService.getInteractionsByReservationId(this.reservationId).subscribe({
      next: (interactions) => {
        const upcomingInteractions = interactions.filter(
          interaction => interaction.interactionStatus === 'upcoming'
        );

        if (upcomingInteractions.length > 0) {
          // Show popup with upcoming interactions and option to mark all as completed
          this.showUpcomingInteractionsPopup(formValue, upcomingInteractions);
        } else {
          // All interactions are completed, proceed with conversion confirmation
          this.showConversionConfirmation(formValue);
        }
      },
      error: (error) => {
        console.error('Error checking upcoming interactions:', error);
        // If error, proceed with normal flow
        this.showConversionConfirmation(formValue);
      }
    });
  }

  private showUpcomingInteractionsPopup(formValue: any, upcomingInteractions: IInteraction[]): void {
    const interactionList = upcomingInteractions.map(interaction => 
      `• ${this.getInteractionTypeDisplay(interaction)} on ${this.formatDate(interaction.interactionDate)} at ${this.formatTime(interaction.interactionTime)}`
    ).join('\n');

    this.upcomingInteractionsMessage = `You have ${upcomingInteractions.length} upcoming interaction(s) that need to be completed before converting to contract:\n\n${interactionList}\n\nWould you like to mark all interactions as completed and proceed to contract creation?`;
    this.pendingFormValue = formValue;
    this.pendingUpcomingInteractions = upcomingInteractions;
    this.showUpcomingInteractionsModal = true;
  }

  onConfirmUpcomingInteractions(): void {
    this.markAllInteractionsCompletedAndCreateContract(this.pendingFormValue, this.pendingUpcomingInteractions);
  }

  onCancelUpcomingInteractions(): void {
    this.showUpcomingInteractionsModal = false;
    this.pendingFormValue = null;
    this.pendingUpcomingInteractions = [];
  }

  private markAllInteractionsCompletedAndCreateContract(formValue: any, upcomingInteractions: IInteraction[]): void {
    if (!this.reservationId) {
      console.error('Reservation ID is not available');
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Reservation ID not found. Cannot mark interactions as completed.'
      });
      return;
    }

    this.isMainButtonLoading = true;
    
    this.interactionService.updateAllInteractionsToCompleted(this.reservationId).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `${response.modifiedCount || upcomingInteractions.length} interactions marked as completed!`
        });
        
        // Now process the current interaction and redirect to contract creation
        this.processInteraction(formValue, true);
      },
      error: (error) => {
        console.error('Error updating interactions:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to mark interactions as completed. Please try again.'
        });
        this.isMainButtonLoading = false;
      }
    });
  }

  private showConversionConfirmation(formValue: any): void {
    this.confirmationService.confirm({
      message: 'You have selected "Converted" status. This will redirect you to create a contract. Do you want to continue?',
      header: 'Create Contract',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept: () => {
        this.processInteraction(formValue, true);
      },
      reject: () => {
      }
    });
  }

  private processInteraction(formValue: any, redirectToContract: boolean = false): void {
    this.isMainButtonLoading = true;
    
    const interactionData: Partial<IInteraction> = {
      interactionType: formValue.interactionType,
      interactionDate: formValue.interactionDate,
      interactionTime: formValue.interactionTime,
      interactionStatus: formValue.interactionStatus,
      interactionNotes: formValue.interactionNotes,
      documents: formValue.documents,
      reservationStatus: formValue.reservationStatus,
      reservationId: this.reservationId
      // subscriptionId will be automatically added by the backend from the authenticated user
    };

    this.interactionService.createInteraction(interactionData as IInteraction).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: this.isCompletedStatus ? 'Interaction completed successfully!' : 'Interaction scheduled successfully!'
        });
        this.isMainButtonLoading = false;

        if (redirectToContract) {
          // Redirect to contract creation page with reservationId
          this.router.navigate(['/admin/add-edit-contract', this.reservationId]);
        } else {
          // Always redirect to view reservation page
          setTimeout(() => {
            this.router.navigate(['/admin/view-reservation', this.reservationId]);
          }, 2000);
        }
      },
      error: (error) => {
        console.error('Error creating interaction:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to schedule interaction. Please try again.'
        });
        this.isMainButtonLoading = false;
      }
    });
  }

  onSaveDraft(): void {
    // Save as draft logic - you can implement this later
    this.messageService.add({
      severity: 'info',
      summary: 'Info',
      detail: 'Draft saved successfully!'
    });
  }

  onJustSave(): void {
    if (this.sheduleInteractionForm.valid && this.reservationId) {
      const formValue = this.sheduleInteractionForm.value;
      
      // Only override reservation status if interaction is not completed
      const modifiedFormValue = { 
        ...formValue, 
        reservationStatus: this.isCompletedStatus ? formValue.reservationStatus : 'ongoing' 
      };
      this.processJustSaveInteraction(modifiedFormValue);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields.'
      });
    }
  }

  processJustSaveInteraction(formValue: any): void {
    this.isJustSaveButtonLoading = true;
    
    const interactionData: Partial<IInteraction> = {
      interactionType: formValue.interactionType,
      interactionDate: formValue.interactionDate,
      interactionTime: formValue.interactionTime,
      interactionStatus: formValue.interactionStatus,
      interactionNotes: formValue.interactionNotes,
      documents: formValue.documents,
      reservationStatus: formValue.reservationStatus,
      reservationId: this.reservationId
      // subscriptionId will be automatically added by the backend from the authenticated user
    };

    this.interactionService.createInteraction(interactionData as IInteraction).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: this.isCompletedStatus ? 'Interaction completed successfully!' : 'Interaction scheduled successfully!'
        });
        this.isJustSaveButtonLoading = false;

        // Always redirect to view reservation for "Just Save"
        setTimeout(() => {
          this.router.navigate(['/admin/view-reservation', this.reservationId]);
        }, 2000);
      },
      error: (error) => {
        console.error('Error creating interaction:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to schedule interaction. Please try again.'
        });
        this.isJustSaveButtonLoading = false;
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/admin/view-reservation', this.reservationId]);
  }

  // File upload methods
  onFileUpload(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.handleFileUpload(file);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileUpload(files[0]);
    }
  }

  triggerFileInput(): void {
    const fileInput = document.getElementById('file-input-schedule') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  private handleFileUpload(file: File): void {
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'File size must be less than 10MB'
      });
      return;
    }

    this.isFileUploading = true;
    this.uploadedFile = file;
    
    // Simulate file upload (replace with actual upload logic)
    setTimeout(() => {
      this.uploadedFileUrl = URL.createObjectURL(file);
      this.isFileUploading = false;
      
      // Update form control
      const currentDocs = this.sheduleInteractionForm.get('documents')?.value || [];
      this.sheduleInteractionForm.patchValue({
        documents: [...currentDocs, file.name]
      });
      
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'File uploaded successfully!'
      });
    }, 2000);
  }

  removeUploadedFile(): void {
    this.uploadedFile = null;
    this.uploadedFileUrl = null;
    this.sheduleInteractionForm.patchValue({ documents: [] });
  }

  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  // Getter to check if status is completed
  get isCompletedStatus(): boolean {
    return this.sheduleInteractionForm.get('interactionStatus')?.value === 'completed';
  }

  // Getter to check if reservation status is required
  get isReservationStatusRequired(): boolean {
    return this.isCompletedStatus;
  }

  // Getter to get the main button text based on reservation status
  get mainButtonText(): string {
    const reservationStatus = this.sheduleInteractionForm.get('reservationStatus')?.value;
    
    switch (reservationStatus) {
      case 'Converted':
        return 'Save';
      case 'Not Interested':
        return 'Save';
      case 'Interested':
        return 'Save & Schedule Follow-up';
      default:
        return this.isCompletedStatus ? 'Complete Interaction' : 'Schedule Interaction';
    }
  }

  // Getter to check if we should show the second button (for Interested status)
  get shouldShowSecondButton(): boolean {
    const reservationStatus = this.sheduleInteractionForm.get('reservationStatus')?.value;
    return reservationStatus === 'Interested';
  }

  // Getter to get the second button text
  get secondButtonText(): string {
    return 'Just Save';
  }

  getReservationStatusDescription(): string {
    const selectedStatus = this.sheduleInteractionForm.get('reservationStatus')?.value;
    
    switch (selectedStatus) {
      case 'Converted':
        return 'Customer has successfully converted and will be redirected to create a contract.';
      case 'Not Interested':
        return 'Customer is not interested in the service. This will close the reservation.';
      case 'Interested':
        return 'Customer is interested and you will be redirected to schedule a follow-up interaction.';
      default:
        return '';
    }
  }

  formatDate(dateString: string | Date): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }

  formatTime(timeString: string): string {
    if (!timeString) return 'N/A';
    
    // Convert 24-hour format to 12-hour format with AM/PM
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    
    return `${displayHour}:${minutes} ${ampm}`;
  }

  getInteractionTypeDisplay(interaction: IInteraction): string {
    if (!interaction.interactionType) return 'N/A';
    return interaction.interactionType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
}
